import { Network } from "./types";

/**
 * Per-token data for the Polygon payments contract.
 *
 * THIS IS A TABLE, NOT A MODULE PER TOKEN. There is one payments contract and it is multi-token
 * internally (its `supportedTokens` registry; `payV2` resolves the token from the card's signed
 * blob), so VIP and USDC are two rows here rather than two integrations. Everything that talks to
 * the contract — ./payments/* — is token-agnostic and takes an address.
 */

/** The contract's default settlement token per network, i.e. its own `tokenAddress()`. */
export const DEFAULT_TOKEN_ADDRESS: Record<Network, string> = {
  mainnet: "0x6860c34db140DB7B589DaDA38859a1d3736bbE3F",
  testnet: "0xd278B77D630cFC02d8855b7402fC254c7AF5F744",
};

export function defaultTokenAddress(network: Network): string {
  return DEFAULT_TOKEN_ADDRESS[network];
}

/**
 * Vendor-SIGNED token records the Keyfob displays while the holder approves a transfer, keyed by
 * network then by LOWER-CASED ERC-20 address.
 *
 * Keyed by ADDRESS, not symbol: the blob itself embeds the token's address, decimals and ticker, so
 * the address is the one key that cannot disagree with its own record. A symbol could be repointed
 * in the off-chain registry and would then select a record for a different token.
 *
 * Each record is signed with the vendor's key, so one CANNOT be produced here for a new token. A
 * token absent from this map has no record, and every signing path must REFUSE rather than sign
 * without one — see tokenRecordFor. The device does not refuse on its own: evm/signing.ts sets
 * `isToken = tokenBinary ? 1 : 0`, so a missing blob makes it sign as a plain transaction and
 * display nothing, i.e. the holder blind-signs a real ERC-20 transfer.
 *
 * A row may exist with an EMPTY binary: that is "we know the token, the vendor has not signed a
 * record for it yet", which is testnet USDC's state. tokenRecordFor collapses empty to undefined
 * so it refuses exactly like an absent row.
 */
export const TOKEN_RECORDS: Record<Network, Record<string, string>> = {
  mainnet: {
    // VIP
    "0x6860c34db140db7b589dada38859a1d3736bbe3f":
      "00009d00030e8f9f9ba525a600000002050081030e8f9f9ba525a6232f1fc3244e270aeea6b05edee62380438f28f0017ee9e5bae958042422a37894a3fad6e4e7327a2542cc3bd0ddab1a786c8f6a42f2df7f8891f8c8e836f455ffffffff00008ae3eb2a6af2866468428066c6b8063765abe04ac65e7b5cfe2240247d64e4a2c449920f7265057f4582f0d9f11fa2a0cd13d5f2be2bd977b031b9ec122155d4010089000000000000006860c34db140db7b589dada38859a1d3736bbe3f07001200000056495034799696f24225854ea154a069b6378eb829864504546014e4f4fb384e3d2f714ac61b319f7e56e7ba53a103514ec1e9a92c52e0d0bf52d67af359e869644e47",
    // USDC
    "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359":
      "00009d00030e8f9f9ba525a600000000050081011863505a256dd7acaca63a182fc98485b416b4a7d9a21744bfe9e1681b145ab6d643ff910bd04f32e92825ca3581ebd0f77726ac2f257c9e65fffe2e06b2bad860cb362d892433ffffffff0000cb3dfe5702ba466dd289f12016a70cca566015cb43c5cec1f9ca959ba01a901083efe16364d8cd549d567b6db23e09308dbb7e72fa7984292d55e9d7bd88c7aa010089000000000000003c499c542cef5e3811e1192ce70d8cc03d5c3359080006000000555344437b980df46658b0385ccd1f0fa6e4536db963526f1a70c9011bc50d58eff56f4a2540d16abf4ec15542a71208c91a829c474f1e29859a95c0707507eaef388b89",
  },
  testnet: {
    // VIP
    "0xd278b77d630cfc02d8855b7402fc254c7af5f744":
      "00009d00030e8f9f9ba525a600000002050081030e8f9f9ba525a6232f1fc3244e270aeea6b05edee62380438f28f0017ee9e5bae958042422a37894a3fad6e4e7327a2542cc3bd0ddab1a786c8f6a42f2df7f8891f8c8e836f455ffffffff00008ae3eb2a6af2866468428066c6b8063765abe04ac65e7b5cfe2240247d64e4a2c449920f7265057f4582f0d9f11fa2a0cd13d5f2be2bd977b031b9ec122155d401008238010000000000d278b77d630cfc02d8855b7402fc254c7af5f744070012000000564950b3819ef9b821326b6f978539f1ebf874ebf85010055b1dc0c157f7fdad3de9e78690e5a23d96d6dc2bd9fe2e358450099a3575fb77576d4ecc75605e3492fe42",
    // USDC — metadata is known (6 decimals, ticker USDC, chainId 80002) but the vendor has not
    // issued a signed record for Amoy yet, so the blob is empty and every signing path refuses.
    // Paste the binary here when it arrives; nothing else needs to change.
    "0x41e94eb019c0762f9bfcf9fb1e58725bfb0e7582":
      "00009d00030e8f9f9ba525a600000002050081030e8f9f9ba525a6232f1fc3244e270aeea6b05edee62380438f28f0017ee9e5bae958042422a37894a3fad6e4e7327a2542cc3bd0ddab1a786c8f6a42f2df7f8891f8c8e836f455ffffffff00008ae3eb2a6af2866468428066c6b8063765abe04ac65e7b5cfe2240247d64e4a2c449920f7265057f4582f0d9f11fa2a0cd13d5f2be2bd977b031b9ec122155d40100823801000000000041e94eb019c0762f9bfcf9fb1e58725bfb0e7582080006000000555344431c8da14b51429e5d44b8e35eade72f3ab25f1346535110d85608c9f03ef81cf3fb5b0ff47aa2de5991d44adccd2d2adcc4da3080ebd3704b42a467a9cca5246d",
  },
};

/**
 * The signed record for a token, or undefined when the vendor has not issued one.
 *
 * undefined is the REFUSE signal. Callers must not pass it on to KeyfobSigner: the device treats a
 * missing blob as "not a token transfer" and signs blind.
 */
export function tokenRecordFor(
  network: Network,
  tokenAddress: string | undefined,
): string | undefined {
  if (!tokenAddress) return undefined;
  const record = TOKEN_RECORDS[network]?.[tokenAddress.toLowerCase()];
  // An EMPTY string is "registered but not yet signed" — testnet USDC's current state — and must
  // collapse to undefined here. Returning "" would leave callers that test `!== undefined`
  // handing an empty blob to KeyfobSigner, where `isToken = tokenBinary ? 1 : 0` makes the device
  // sign blind. Normalising at the source means no caller can get that wrong.
  return record ? record : undefined;
}
